import React from 'react';
import { useModalBehavior } from 'hooks';
import BookingFlow from './Booking/BookingFlow';

/**
 * BookingFlowModal - Modal wrapper for BookingFlow component
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback to close the modal
 * @param {number} props.teacherId - Teacher's ID
 * @param {string} props.teacherUuid - Teacher's webhook UUID
 * @param {string} props.referrerTeacherSlug - Referrer teacher slug (for tracking)
 * @param {string} props.referrerStudentUuid - Referrer student UUID (for tracking)
 * @param {Object} props.translations - Translation strings from backend
 * @param {Array} props.timezoneOptions - Timezone options from backend
 * @param {Function} props.onComplete - Callback when booking is completed
 */
const BookingFlowModal = ({
  isOpen,
  onClose,
  teacherId,
  teacherUuid,
  referrerTeacherSlug,
  referrerStudentUuid,
  translations,
  timezoneOptions = [],
  onComplete
}) => {
  // Use reusable modal behavior hook
  const { handleOverlayClick } = useModalBehavior(isOpen, onClose);

  if (!isOpen) return null;

  const t = translations?.bookings?.booking_modal || {};

  const handleBookingComplete = (bookingData) => {
    // Close modal first
    onClose();

    // Call parent's onComplete callback if provided
    if (onComplete) {
      onComplete(bookingData);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={handleOverlayClick}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {t.subtitle || 'Select a course to book'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Body - BookingFlow Component */}
        <div className="flex-1 overflow-y-auto bg-gray-50 p-4">
          <BookingFlow
            teacherId={teacherId}
            teacherUuid={teacherUuid}
            referrerTeacherSlug={referrerTeacherSlug}
            referrerStudentUuid={referrerStudentUuid}
            translations={translations?.bookings?.booking_flow || {}}
            timezoneOptions={timezoneOptions}
            onComplete={handleBookingComplete}
          />
        </div>
      </div>
    </div>
  );
};

export default BookingFlowModal;
